import React, { useEffect, useState, useCallback } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { authAPI, memberAPI } from '../utils/authUtils';
import api from '../apiReequest';
import { useNavigate } from 'react-router-dom';
import SafeRichText from '../components/SafeRichText';
import LoadingSpinner from '../components/LoadingSpinner';
import { scrollToForm } from '../utils/adminFormUtils';
import { handleError } from '../utils/errorHandler';
import ErrorDisplay from '../components/ErrorDisplay';
import '../styles/adminCommonStyles.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '/backend/php';
const TINYMCE_API_KEY = process.env.REACT_APP_TINYMCE_API_KEY; // Secure the API key
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB - must match backend limit

export default function AdminMembers() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const refetch = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await memberAPI.getMembers();
      const { normalizeList, normalizeItem } = await import('../utils/responseHelpers');
      setMembers(normalizeList(res));
    } catch (err) {
      setFetchError(err.response?.data?.message || err.message || 'Failed to fetch members');
    } finally {
      setLoading(false);
    }
  }, []);
  const [error, setError] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ id: '', name: '', role: '', bio: '', photo_url: '' });
  const [editImage, setEditImage] = useState(null);
  const [editError, setEditError] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', role: '', bio: '' });
  const [addImage, setAddImage] = useState(null);
  const [addError, setAddError] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    setIsSuperAdmin(authAPI.hasRole('superadmin'));
    setIsAdmin(authAPI.hasRole('admin'));
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Success message helper
  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Add member handlers
  const handleAddInput = (e) => {
    setAddForm({ ...addForm, [e.target.name]: e.target.value });
  };
  const handleAddImage = (e) => {
    const file = e.target.files[0];
    if (file && file.size > MAX_FILE_SIZE) {
      setAddError(`Image size exceeds 100MB limit. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`);
      e.target.value = '';
      return;
    }
    setAddError(null);
    setAddImage(file);
  };
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    setAddError(null);
    try {
      // Send multipart FormData directly to /api/team so backend saves to /images
      const formData = new FormData();
      formData.append('name', addForm.name);
      formData.append('role', addForm.role);
      formData.append('bio', addForm.bio);
      if (addImage) formData.append('image', addImage);
      await memberAPI.createMember(formData, true);
      setShowAddModal(false);
      setAddForm({ name: '', role: '', bio: '' });
      setAddImage(null);
      showSuccess('Member added successfully!');
      await refetch();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to add member';
      setAddError(typeof errorMsg === 'string' ? errorMsg : 'Failed to add member');
    } finally {
      setAddLoading(false);
    }
  };

  // Edit member handlers
  const handleEditClick = (member) => {
    setEditForm({ id: member.id, name: member.name, role: member.role, bio: member.bio, photo_url: member.photo_url || '' });
    setEditImage(null);
    setShowEditModal(true);
    setEditError(null);
  };
  const handleEditInput = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };
  const handleEditImage = (e) => {
    const file = e.target.files[0];
    if (file && file.size > MAX_FILE_SIZE) {
      setEditError(`Image size exceeds 100MB limit. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`);
      e.target.value = '';
      return;
    }
    setEditError(null);
    setEditImage(file);
  };
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setEditError(null);
    try {
      let resp;
      // If an image was selected, send multipart FormData, otherwise send JSON
      if (editImage) {
        // Update text fields first (PUT JSON), then upload photo via dedicated POST endpoint
        const updateData = { name: editForm.name, role: editForm.role, bio: editForm.bio };
        resp = await memberAPI.updateMember(editForm.id, updateData, false);

        try {
          const photoForm = new FormData();
          photoForm.append('photo', editImage);
          await api.post(`/team/${editForm.id}/photo`, photoForm);
        } catch (photoErr) {
          const photoErrMsg = photoErr.response?.data?.error || photoErr.message || 'Photo upload failed';
          setEditError(photoErrMsg);
          return;  // Stop here - don't show success if photo upload failed
        }
      } else {
        const updateData = { name: editForm.name, role: editForm.role, bio: editForm.bio };
        resp = await memberAPI.updateMember(editForm.id, updateData, false);
      }
      console.log('member update response:', resp && resp.data ? resp.data : resp);
      // If API returned the updated member, prefer that; otherwise use the form values
      const { normalizeItem } = await import('../utils/responseHelpers');
      const updatedMember = normalizeItem(resp) || { id: editForm.id, name: editForm.name, role: editForm.role, bio: editForm.bio };
      // Update local list immediately for UX responsiveness
      setMembers(prev => prev.map(m => (m.id === editForm.id ? { ...m, ...updatedMember } : m)));
      showSuccess('Member updated successfully!');
      setShowEditModal(false);
      
      // Refresh from server to ensure consistency
      await refetch();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to update member';
      setEditError(typeof errorMsg === 'string' ? errorMsg : 'Failed to update member');
    } finally {
      setEditLoading(false);
    }
  };

  // Delete member
  const handleDeleteMember = async (memberId, memberName) => {
    if (!isSuperAdmin) {
      alert('Access denied. Only superadmin can delete members.');
      return;
    }
    const confirmDelete = window.confirm(`Are you sure you want to delete "${memberName}"? This action cannot be undone.`);
    if (!confirmDelete) return;
    setDeleteLoading(memberId);
    setError(null);
    try {
      await memberAPI.deleteMember(memberId);
      await refetch();
      showSuccess('Member deleted successfully!');
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to delete member';
      setError(typeof errorMsg === 'string' ? errorMsg : 'Failed to delete member');
    } finally {
      setDeleteLoading(null);
    }
  };

  // Logout
  const handleLogout = () => {
    authAPI.logout();
    navigate('/admin/login');
  };

  // Permission logic
  const canEdit = isSuperAdmin || isAdmin;
  const canAdd = isSuperAdmin;
  const canDelete = isSuperAdmin;

  return (
  <div className="w-full max-w-screen-2xl mx-auto px-1 md:px-2 py-2 mt-0 landscape:px-4 landscape:py-4 flex flex-col items-center justify-center">
      <div className="w-full flex flex-col items-center sm:items-start gap-4 sm:flex-row sm:justify-between mb-6 md:gap-0">
        <h2 className="text-3xl md:text-4xl font-bold text-black mb-2 text-center sm:text-left drop-shadow-lg">Founders</h2>
        <div className="flex items-center gap-2 sm:gap-4 justify-center sm:justify-end">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${isSuperAdmin ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-blue-100 text-blue-800 border border-blue-300'}`}>{isSuperAdmin ? 'Super Admin' : isAdmin ? 'Admin' : 'User'}</span>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-700 text-white rounded-xl hover:scale-105 shadow-lg transition-all duration-300 border border-red-300"
          >
            Logout
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="admin-alert-success">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="admin-alert-error">
          <strong>Error:</strong> <ErrorDisplay error={error} className="text-red-700 font-medium" />
        </div>
      )}

      {canAdd && (
        <button
          className="admin-btn-primary-lg mb-6"
          onClick={() => {
            setShowAddModal(true);
            setTimeout(() => {
              scrollToForm();
            }, 0);
          }}
        >
          + Add New Member
        </button>
      )}

      {showAddModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-content admin-modal-form" id="add-member-form">
            <h3 className="admin-modal-title">Add New Member</h3>
            <form onSubmit={handleAddSubmit}>
              <div className="mb-4">
                <label className="admin-label">Name</label>
                <input
                  type="text"
                  name="name"
                  value={addForm.name}
                  onChange={handleAddInput}
                  className="admin-input"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="admin-label">Role</label>
                <input
                  type="text"
                  name="role"
                  value={addForm.role}
                  onChange={handleAddInput}
                  className="admin-input"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="admin-label">Bio</label>
                <Editor
                  value={addForm.bio}
                  onEditorChange={val => setAddForm(f => ({ ...f, bio: val }))}
                  init={{
                    apiKey: TINYMCE_API_KEY,
                    height: 440,
                    menubar: false,
                    branding: false,
                    analytics: false,
                    external_plugins: {},
                    plugins: 'advlist autolink lists link image charmap anchor searchreplace visualblocks code fullscreen insertdatetime media table help wordcount',
                    toolbar:
                      'undo redo | formatselect | bold italic backcolor | \
                      alignleft aligncenter alignright alignjustify | \
                      bullist numlist outdent indent | removeformat | help'
                  }}
                />
              </div>
              <div className="mb-4">
                <label className="admin-label">Photo</label>
                <input
                  type="file"
                  name="image"
                  accept="image/*"
                  onChange={handleAddImage}
                  className="admin-input"
                />
              </div>
              {addError && (
                <div className="admin-alert-error-inline">
                  {addError}
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={() => {
                    setShowAddModal(false);
                    setAddError(null);
                  }}
                  disabled={addLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn-primary"
                  disabled={addLoading}
                >
                  {addLoading ? 'Adding...' : 'Add Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="admin-modal-overlay-scroll">
          <div className="admin-modal-content">
            <h3 className="admin-modal-title">Edit Member</h3>
            <form onSubmit={handleEditSubmit}>
              <div className="mb-4">
                <label className="admin-label">Name</label>
                <input
                  type="text"
                  name="name"
                  value={editForm.name}
                  onChange={handleEditInput}
                  className="admin-input"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="admin-label">Role</label>
                <input
                  type="text"
                  name="role"
                  value={editForm.role}
                  onChange={handleEditInput}
                  className="admin-input"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="admin-label">Bio</label>
                <Editor
                  value={editForm.bio}
                  onEditorChange={val => setEditForm(f => ({ ...f, bio: val }))}
                  init={{
                    apiKey: TINYMCE_API_KEY,
                    height: 440,
                    menubar: false,
                    branding: false,
                    analytics: false,
                    external_plugins: {},
                    plugins: 'advlist autolink lists link image charmap anchor searchreplace visualblocks code fullscreen insertdatetime media table help wordcount',
                    toolbar:
                      'undo redo | formatselect | bold italic backcolor | \
                      alignleft aligncenter alignright alignjustify | \
                      bullist numlist outdent indent | removeformat | help'
                  }}
                />
              </div>
              <div className="mb-4">
                <label className="admin-label">Photo</label>
                {editForm.photo_url && (
                  <div className="admin-image-preview-container">
                    <p className="text-sm font-medium text-gray-600 mb-2">Current Photo:</p>
                    <img
                      src={(() => {
                        if (!isNaN(Number(editForm.photo_url))) {
                          return `${API_BASE}/image/${editForm.photo_url}?t=${Date.now()}`;
                        }
                        if (editForm.photo_url && editForm.photo_url.startsWith('/images/')) {
                          return editForm.photo_url + `?t=${Date.now()}`;
                        }
                        if (editForm.photo_url && editForm.photo_url.startsWith('/api/')) {
                          return `${API_BASE}${editForm.photo_url}?t=${Date.now()}`;
                        }
                        if (editForm.photo_url && editForm.photo_url.startsWith('/')) {
                          return `${API_BASE}${editForm.photo_url}?t=${Date.now()}`;
                        }
                        return editForm.photo_url;
                      })()}
                      alt="Current"
                      className="admin-image"
                    />
                  </div>
                )}
                <input
                  type="file"
                  name="photo"
                  accept="image/*"
                  onChange={handleEditImage}
                  className="admin-input"
                />
                {editImage && (
                  <div className="admin-image-preview-new">
                    <p className="text-sm font-medium text-blue-600 mb-2">New Photo Preview:</p>
                    <img
                      src={URL.createObjectURL(editImage)}
                      alt="New"
                      className="admin-image-new"
                    />
                  </div>
                )}
              </div>
              {editError && (
                <div className="admin-alert-error-inline">
                  {editError}
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditError(null);
                  }}
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn-success"
                  disabled={editLoading}
                >
                  {editLoading ? 'Updating...' : 'Update Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading && (
        <LoadingSpinner message="Loading members..." bgColor="bg-white" spinnerColor="border-[#73ab40]" py={12} />
      )}

      {!loading && (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead className="admin-table-head">
              <tr>
                <th className="admin-table-header-cell">Name</th>
                <th className="admin-table-header-cell">Role</th>
                <th className="admin-table-header-cell">Bio</th>
                <th className="admin-table-header-cell">Photo</th>
                <th className="admin-table-header-cell">Actions</th>
              </tr>
            </thead>
            <tbody className="admin-table-body-row">
              {members.length === 0 ? (
                <tr>
                  <td colSpan="5" className="admin-empty-state">
                    <div className="admin-empty-state-title">No members found</div>
                    <div className="admin-empty-state-subtitle">Add your first member to get started!</div>
                  </td>
                </tr>
              ) : (
                members.map((member) => (
                  <tr key={member.id} className="hover:bg-blue-50 transition-colors">
                    <td className="py-2 px-2 font-medium text-gray-900">
                      {member.name && member.name.length > 50 ? member.name.slice(0, 50) + '…' : member.name}
                    </td>
                    <td className="py-2 px-2">
                      {member.role && member.role.length > 50 ? member.role.slice(0, 50) + '…' : member.role}
                    </td>
                    <td className="py-2 px-2 max-w-xs truncate whitespace-pre-line break-words" title={member.bio}>
                      <SafeRichText content={member.bio && member.bio.length > 50 ? member.bio.slice(0, 50) + '…' : member.bio} />
                    </td>
                    <td className="py-2 px-2">
                      {member.photo_url ? (
                        (() => {
                          let src = member.photo_url;
                          if (src && src.startsWith('/api/')) {
                            src = `${API_BASE}${src}?t=${Date.now()}`;
                          }
                          return (
                            <img
                              src={src}
                              alt={member.name}
                              className="h-12 w-12 object-cover rounded-full border"
                            />
                          );
                        })()
                      ) : (
                        <span className="text-gray-400">No photo</span>
                      )}
                    </td>
                    <td className="py-2 px-2">
                      <div className="flex flex-col gap-1 items-stretch justify-center">
                        {canEdit && (
                          <button
                            onClick={() => handleEditClick(member)}
                            className="admin-btn-table-edit"
                          >
                            Edit
                          </button>
                        )}
                        {canDelete ? (
                          <button
                            onClick={() => handleDeleteMember(member.id, member.name)}
                            disabled={deleteLoading === member.id}
                            className="admin-btn-table-danger"
                          >
                            {deleteLoading === member.id ? 'Deleting...' : 'Delete'}
                          </button>
                        ) : (
                          <button
                            disabled
                            className="admin-btn-table-disabled"
                            title="Superadmin role required to delete members">
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
